import { useEffect, useState } from 'react';
import { inviti, type SchedaBiglietto } from '../../api/inviti';
import { ErroreApi, ErroreRete } from '../../api/client';

/*  LA SCHERMATA DEL LINK RICEVUTO PER MAIL.

    Sta fuori dall'applicativo: ci si arriva da `/invito?t=…` senza essere collegati, e
    non c'e' nessun menu, nessuna barra e nessuna sessione. E' l'unica schermata di
    TeraWorks che una persona vede prima di esistere, per cosi' dire.

    IL BIGLIETTO SI LEGGE PRIMA DI CHIEDERE LA PASSWORD. Si potrebbe mostrare subito il
    campo e scoprire solo dopo che il link e' scaduto — e' una chiamata in meno — ma vuol
    dire far scegliere e scrivere due volte una password a qualcuno che poi deve
    ricominciare dalla mail. Meglio dirlo subito.

    E SI SALUTA PER NOME. Non e' cortesia: e' la conferma che il link e' arrivato alla
    persona giusta. Chi apre un invito destinato a un collega — succede, le mail si
    inoltrano — deve vedere il nome del collega e fermarsi.

    IL BIGLIETTO NON SI CONSUMA SE LA PASSWORD NON VA BENE. Chi ne scrive una troppo
    corta riprova da qui, senza tornare alla mail.
*/
export function Invito({ biglietto }: { biglietto: string }) {
  const [scheda, setScheda] = useState<SchedaBiglietto | null>(null);
  const [statoLettura, setStatoLettura] = useState<'attesa' | 'buono' | 'scaduto' | 'guasto'>(
    'attesa',
  );
  const [motivo, setMotivo] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [ripetuta, setRipetuta] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [fatto, setFatto] = useState(false);

  const diverse = ripetuta.length > 0 && password !== ripetuta;

  useEffect(() => {
    if (biglietto.length === 0) {
      setStatoLettura('scaduto');
      return;
    }

    void inviti
      .leggi(biglietto)
      .then((letta) => {
        setScheda(letta);
        setStatoLettura('buono');
      })
      .catch((guasto: unknown) => {
        if (guasto instanceof ErroreRete) {
          setMotivo('TeraWorks non risponde.');
          setStatoLettura('guasto');
          return;
        }
        setStatoLettura('scaduto');
      });
  }, [biglietto]);

  async function salva(evento: React.FormEvent) {
    evento.preventDefault();

    if (password !== ripetuta) {
      setErrore('Le due password non coincidono.');
      return;
    }

    setErrore(null);
    setInCorso(true);

    try {
      await inviti.imposta(biglietto, password);
      setPassword('');
      setRipetuta('');
      setFatto(true);
    } catch (guasto) {
      setErrore(
        guasto instanceof ErroreRete
          ? 'TeraWorks non risponde: la password non è stata impostata.'
          : guasto instanceof ErroreApi
            ? guasto.message
            : 'Non è riuscito.',
      );
      setInCorso(false);
    }
  }

  /*  I riquadri: dentro `.accesso` i figli stanno in fila, perché l'unico figlio
      previsto era il modulo. Senza questo involucro titolo e paragrafi finivano uno
      accanto all'altro su una riga sola — visibile al primo colpo d'occhio, e invisibile
      a TypeScript e ai test.  */
  if (statoLettura === 'attesa') {
    return (
      <div className="accesso">
        <div className="riquadro">
          <p className="attesa">Verifica del link…</p>
        </div>
      </div>
    );
  }

  if (statoLettura === 'guasto') {
    return (
      <div className="accesso">
        <div className="riquadro">
          <h1>TeraWorks non risponde</h1>
          <p>{motivo}</p>
          <p className="nota">Il link resta valido: si può riprovare fra poco.</p>
        </div>
      </div>
    );
  }

  if (statoLettura === 'scaduto') {
    return (
      <div className="accesso">
        <div className="riquadro">
          <h1>Questo link non è più valido</h1>
          <p>È scaduto, oppure è già stato usato una volta.</p>
          <p className="nota">
            Si può chiederne un altro da «password dimenticata» nella schermata di accesso,
            oppure farselo rimandare da chi gestisce le utenze.
          </p>
          <p>
            <a href="/">Vai alla schermata di accesso</a>
          </p>
        </div>
      </div>
    );
  }

  if (fatto) {
    return (
      <div className="accesso">
        <div className="riquadro">
          <h1>Fatto</h1>
          <p>La password è impostata. Ora puoi entrare in TeraWorks.</p>
          <p>
            <a href="/">Vai a TeraWorks</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="accesso">
      <form onSubmit={(e) => void salva(e)}>
        <h1>{scheda?.primoAccesso === true ? 'Benvenuto in TeraWorks' : 'Nuova password'}</h1>

        <p className="nota">
          {scheda?.nomeCompleto} — il tuo nome utente è <strong>{scheda?.codice}</strong>.
          {scheda?.primoAccesso === true
            ? ' Scegli la password con cui entrerai.'
            : ' Scegli la password nuova.'}
        </p>

        <label className="campo-blocco">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            autoFocus
            required
          />
        </label>

        <label className="campo-blocco">
          <span>Ripetila</span>
          <input
            type="password"
            value={ripetuta}
            onChange={(e) => setRipetuta(e.target.value)}
            autoComplete="new-password"
            aria-invalid={diverse}
            required
          />
        </label>

        {diverse && <p className="nota">Le due non coincidono ancora.</p>}
        {errore !== null && <p className="errore">{errore}</p>}

        <button type="submit" className="primario" disabled={inCorso || diverse}>
          {inCorso ? 'Salvataggio…' : 'Imposta la password'}
        </button>
      </form>
    </div>
  );
}
